import { useEffect, useRef } from 'react';

const ConstellationBackground = ({ darkMode }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    let width, height, dpr;
    let nodes = [];
    let animationId;

    const nodeColor = darkMode ? '186, 230, 253' : '14, 116, 144';
    const lineColor = darkMode ? '125, 211, 252' : '56, 189, 248';

    const setSize = () => {
      dpr = window.devicePixelRatio || 1;
      width = window.innerWidth;
      height = window.innerHeight;

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const createNodes = () => {
      const density = Math.min(70, Math.floor((width * height) / 22000));

      nodes = Array.from({ length: density }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.6 + 0.6
      }));
    };

    const maxLinkDist = 160;

    const step = () => {
      ctx.clearRect(0, 0, width, height);

      nodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;

        if (n.x < 0 || n.x > width) n.vx *= -1;
        if (n.y < 0 || n.y > height) n.vy *= -1;
      });

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i];
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;

          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < maxLinkDist) {
            const opacity = (1 - dist / maxLinkDist) * 0.35;

            ctx.strokeStyle = `rgba(${lineColor}, ${opacity})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      nodes.forEach((n) => {
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${nodeColor}, 0.6)`;
        ctx.fill();
      });

      animationId = requestAnimationFrame(step);
    };

    const handleResize = () => {
      setSize();
      createNodes();
    };

    setSize();
    createNodes();
    step();

    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationId);

      window.removeEventListener('resize', handleResize);
    };
  }, [darkMode]);

  return <canvas ref={canvasRef} className='constellation-bg' aria-hidden='true' />;
};

export default ConstellationBackground;
